import { useCallback, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { m } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Container, Stack, Typography } from "@mui/material";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import { MotionContainer, varBounce } from "../../components/animate";
import { SeverErrorIllustration } from "../../assets/illustrations";

const LOGOUT_ONCE_KEY = "hh_logout_once";
const AUTH_RETURN_PATH_KEY = "auth0_return_path";

export default function LogoutPage() {
  const { isAuthenticated, isLoading, logout } = useAuth0();

  // Auth0 logout must return to an Allowed Logout URL.
  const returnTo = useMemo(() => `${window.location.origin}/logout`, []);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    // Prevent redirect loops: if we already initiated logout from this page,
    // don't keep calling logout on re-mount.
    const alreadyInitiated = sessionStorage.getItem(LOGOUT_ONCE_KEY) === "1";
    if (alreadyInitiated) return;
    sessionStorage.setItem(LOGOUT_ONCE_KEY, "1");

    logout({ logoutParams: { returnTo } });
  }, [isAuthenticated, isLoading, logout, returnTo]);

  const handleBackToLogin = useCallback(() => {
    try {
      sessionStorage.removeItem(LOGOUT_ONCE_KEY);
      // After login, always land on root.
      sessionStorage.setItem(AUTH_RETURN_PATH_KEY, "/");
    } catch (e) {
      // ignore
    }
    window.location.assign("/");
  }, []);

  if (isLoading) return <LoadingScreen />;

  return (
    <>
      <Helmet>
        <title>Logged Out | Hammerhead</title>
      </Helmet>

      <Container component="main">
        <Stack
          sx={{
            py: 12,
            m: "auto",
            maxWidth: 420,
            minHeight: "100vh",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <MotionContainer>
            <m.div variants={varBounce().in}>
              <Typography variant="h3" paragraph>
                You’ve been logged out
              </Typography>
            </m.div>

            <m.div variants={varBounce().in}>
              <Typography sx={{ color: "text.secondary" }}>
                Your session is no longer valid. Please sign in again to continue.
              </Typography>
            </m.div>

            <m.div variants={varBounce().in}>
              <SeverErrorIllustration
                sx={{
                  height: 260,
                  my: { xs: 5, sm: 10 },
                }}
              />
            </m.div>

            <Button size="large" variant="contained" onClick={handleBackToLogin}>
              Back to login
            </Button>
          </MotionContainer>
        </Stack>
      </Container>
    </>
  );
}
